import axios from 'axios';
import axiosInstance from '../axios/axios';
import {
  CompanyReviewDecision,
  CompanyReviewDecisionType,
  CompanyMatch,
  CompanyReviewWithMatches,
} from '../../types/companyReview';

const COMPANY_REVIEW_ENDPOINT = 'api/company-review/decision';
const TABLE_API_URL = 'api/azure-table-operations';

export interface CompanyReviewDecisionRequest {
  ClientId: string;
  ReviewId: string;
  Decision: CompanyReviewDecisionType;
  SelectedCompanyId?: string;
  NewCompanyName?: string;
}

export interface CompanyReviewServiceError {
  message: string;
  statusCode?: number;
}

// Raw entity from Azure Table Storage
interface CompanyReviewTableEntity {
  PartitionKey: string;
  RowKey: string;
  ExtractedCompanyName: string;
  CompanyGST?: string;
  FileName?: string;
  BatchId?: string;
  Status: string;
  MatchingCompaniesJson?: string;
}

// PascalCase company match from JSON (backend format)
interface CompanyMatchRaw {
  CompanyId: string;
  CompanyName: string;
  CompanyGst?: string;
  SimilarityScore: number;
}

export const companyReviewService = {
  async submitDecision(
    clientId: string,
    decision: CompanyReviewDecision,
  ): Promise<void> {
    try {
      const request: CompanyReviewDecisionRequest = {
        ClientId: clientId,
        ReviewId: decision.ReviewId,
        Decision: decision.Decision,
        SelectedCompanyId: decision.SelectedCompanyId,
        NewCompanyName: decision.NewCompanyName,
      };

      await axiosInstance.post(COMPANY_REVIEW_ENDPOINT, request);
    } catch (error: unknown) {
      const serviceError = handleCompanyReviewError(error);
      throw serviceError;
    }
  },

  async getRequiredReviews(
    clientId: string,
    batchId: string,
  ): Promise<CompanyReviewWithMatches[]> {
    try {
      const request = {
        tableName: 'CompanyReview',
        operationName: 'getbyquery',
        query: `PartitionKey eq '${clientId.toLowerCase()}' and BatchId eq '${batchId}'`,
      };

      const response = await axiosInstance.post<CompanyReviewTableEntity[]>(
        TABLE_API_URL,
        request,
      );

      // Count completed items for pagination
      const completedCount = response.data.filter(
        entity => entity.Status === 'Completed',
      ).length;

      // Filter out completed items and transform raw table data to CompanyReviewWithMatches format
      const pendingReviews = response.data
        .filter(entity => entity.Status !== 'Completed')
        .map((entity, index) => {
          let matchingCompanies: CompanyMatch[] = [];
          if (entity.MatchingCompaniesJson) {
            try {
              const rawMatches: CompanyMatchRaw[] = JSON.parse(
                entity.MatchingCompaniesJson,
              );
              // Transform PascalCase to camelCase
              matchingCompanies = rawMatches.map(raw => ({
                companyId: raw.CompanyId,
                companyName: raw.CompanyName,
                companyGST: raw.CompanyGst,
                similarityScore: raw.SimilarityScore,
              }));
            } catch {
              console.warn(
                'Failed to parse MatchingCompaniesJson for review',
                entity.RowKey,
              );
            }
          }

          return {
            reviewId: entity.RowKey,
            extractedCompanyName: entity.ExtractedCompanyName,
            companyGST: entity.CompanyGST,
            fileName: entity.FileName,
            batchId: entity.BatchId,
            matchingCompanies,
            currentPage: completedCount + index + 1,
            totalItems: response.data.length,
            pageSize: 1,
          };
        });

      return pendingReviews;
    } catch (error: unknown) {
      const serviceError = handleCompanyReviewError(error);
      throw serviceError;
    }
  },
};

function handleCompanyReviewError(error: unknown): CompanyReviewServiceError {
  if (axios.isAxiosError(error)) {
    const statusCode = error.response?.status;

    if (statusCode === 400) {
      return {
        message: 'Invalid request. Please check your selection and try again.',
        statusCode,
      };
    }

    if (statusCode === 404) {
      return {
        message:
          'Review request not found. It may have expired or already been processed.',
        statusCode,
      };
    }

    if (statusCode === 500) {
      return {
        message: 'Server error occurred. Please try again later.',
        statusCode,
      };
    }

    if (error.code === 'ECONNABORTED') {
      return {
        message:
          'Request timed out. Please check your connection and try again.',
      };
    }

    if (!error.response) {
      return {
        message: 'Network error. Please check your internet connection.',
      };
    }

    return {
      message: error.response?.data?.message || 'An unexpected error occurred.',
      statusCode,
    };
  }

  return {
    message: 'An unexpected error occurred while submitting your decision.',
  };
}

export default companyReviewService;
